import {
  ELEVATIONS,
  MARINE_HEATWAVES,
  PATHWAYS,
  REGIONAL_SKI_RISK,
  REVIEWED_ON,
  SEA_LEVEL,
  SEA_TEMPERATURE,
  SKI_RISK,
  SOURCES,
  WARMING,
  type SourceKey,
} from "./evidence";
import { SEASON_SOURCES, SNOW_DAY_PERIODS, SNOW_DAY_STATIONS, SNOW_MASSIFS, SNOW_SCENARIOS, snowReliabilitySeries } from "./season-evidence";

export const FINDING_SOURCES = {
  ...SOURCES,
  reliability: { label: "Spandre et al. · snow reliability", year: 2019, url: SEASON_SOURCES.reliability.url, note: `${SEASON_SOURCES.reliability.detail} ${SEASON_SOURCES.reliability.licence}.`, licence: SEASON_SOURCES.reliability.licence },
  snowDays: { label: "Marty et al. · snow-cover days", year: 2017, url: SEASON_SOURCES.snowDays.url, note: `${SEASON_SOURCES.snowDays.detail} ${SEASON_SOURCES.snowDays.licence}.`, licence: SEASON_SOURCES.snowDays.licence },
} as const;
export const CLIMATE_REUSE_NOTE = "Cite PeakWave and the original sources. Consult the original sources for their reuse terms.";

export const CLIMATE_CHART_IDS = [
  "warming", "snow-risk", "sea-level", "sea-temperature",
  "marine-heatwaves", "coral", "snowline", "snow-elevation", "season-length",
] as const;
export type ClimateChartId = (typeof CLIMATE_CHART_IDS)[number];

export type ClimateFindingRow = Readonly<{
  label: string;
  value: number | null;
  low: number | null;
  high: number | null;
  /** A threshold is never silently turned into an exact value. */
  qualifier?: "approximate" | "greater-than";
  selected: boolean;
}>;

export type ClimateFinding = Readonly<{
  id: ClimateChartId;
  title: string;
  seoTitle: string;
  description: string;
  subtitle: string;
  unit: string;
  period: string;
  baseline: string;
  range: string;
  method: string;
  caveat: string;
  sources: readonly (SourceKey | "reliability" | "snowDays")[];
  rows: readonly ClimateFindingRow[];
  axisMax: number;
  parameters: Readonly<Record<string, string>>;
  reference?: Readonly<{ label: string; value: number }>;
}>;

const PATHWAY_KEYS = ["low", "middle", "high"] as const;

function choice<T extends string>(
  params: URLSearchParams, key: string, allowed: readonly T[], fallback: T,
): T {
  const values = params.getAll(key);
  return values.length === 1 && allowed.some((value) => value === values[0])
    ? values[0] as T : fallback;
}

export function isClimateChartId(value: string): value is ClimateChartId {
  return CLIMATE_CHART_IDS.some((id) => id === value);
}

/** Pure, bounded public evidence. No arbitrary URLs, providers or database reads. */
export function getClimateFinding(id: string, params = new URLSearchParams()): ClimateFinding | null {
  if (!isClimateChartId(id)) return null;
  if (id === "snow-elevation") {
    const snowMassif = choice(params, "snowMassif", SNOW_MASSIFS.map((massif) => massif.id), "chablais");
    const snowScenario = choice(params, "snowScenario", SNOW_SCENARIOS.map((scenario) => scenario.value), "rcp45");
    const snowAltitude = choice(params, "snowAltitude", ["1000", "1500", "1800", "2000", "2500", "3000", "3500"] as const, "1800");
    const massif = SNOW_MASSIFS.find((entry) => entry.id === snowMassif)!;
    const scenario = SNOW_SCENARIOS.find((entry) => entry.value === snowScenario)!;
    return {
      id, title: "Where reliable natural snow moves uphill.",
      seoTitle: "Snow Reliability Elevation: Mountain Futures",
      description: "Compare published natural-snow reliability elevations by mountain area, emissions scenario and period, with study criteria and source downloads.",
      subtitle: `${massif.name} · ${scenario.label}`, unit: "m",
      period: "1986–2005 reference; 2030–2050 and 2080–2100 projections",
      baseline: "1986–2005 modelled HIST reference",
      range: "100-day groomed-snow criterion in 7/10 winters; not a confidence interval",
      method: `Spandre et al. (2019), Appendix A, Tables A1–A3. Groomed natural snow: at least 100 days with 100 kg/m², 15 December to 15 April, in seven winters out of ten. The selected comparison altitude of ${snowAltitude} m is context, not a model output.`,
      caveat: "Regional modelled reliability elevations are not universal snowlines, operating dates or resort closure predictions.",
      sources: ["reliability"], axisMax: 3500, parameters: { snowMassif, snowScenario, snowAltitude },
      reference: { label: "Your comparison altitude", value: Number(snowAltitude) },
      rows: snowReliabilitySeries(massif, snowScenario).map((row) => ({ label: row.label, value: row.elevationM, low: null, high: null, selected: row.key !== "reference" })),
    };
  }
  if (id === "season-length") {
    const seasonStation = choice(params, "seasonStation", SNOW_DAY_STATIONS.map((station) => station.id), "davos");
    const seasonPeriod = choice(params, "seasonPeriod", ["near", "middle", "late"] as const, "late");
    const station = SNOW_DAY_STATIONS.find((entry) => entry.id === seasonStation)!;
    return {
      id, title: "How many days keep their snow?",
      seoTitle: "Snow-Cover Days: Future Season Projections",
      description: "Compare published Swiss station snow-cover days across future periods, with the SRES A2 scenario, reference baseline and original study data.",
      subtitle: `${station.name} · SRES A2 emissions scenario`, unit: "days",
      period: `Selected: ${SNOW_DAY_PERIODS.find((period) => period.key === seasonPeriod)!.label}`,
      baseline: "1999–2012 modelled reference",
      range: "Mean days with at least 5 cm of snow; no uncertainty interval supplied here",
      method: "Marty et al. (2017), supplement Table S2. These are the published mean snow-cover day counts under SRES A2; the paper's main text uses the term medians. Periods overlap and are not an annual time series.",
      caveat: "Snow-cover days do not establish opening or closing dates. This older SRES A2 scenario is not an SSP or a current-policy forecast.",
      sources: ["snowDays"], axisMax: 300, parameters: { seasonStation, seasonPeriod },
      rows: SNOW_DAY_PERIODS.map((period) => ({ label: `${period.label}${period.key === "reference" ? " · reference" : ""}`, value: station.days[period.key], low: null, high: null, selected: period.key === seasonPeriod })),
    };
  }
  if (id === "warming") {
    const pathway = choice(params, "pathway", PATHWAY_KEYS, "middle");
    const period = choice(params, "period", ["2050", "2090"] as const, "2090");
    return {
      id, title: "A warmer world. Different winters.",
      seoTitle: "Global Warming Projections: Compare Pathways",
      description: "Compare published global warming projections by emissions pathway, with IPCC ranges, baselines, source links and chart downloads.",
      subtitle: "Global surface temperature change", unit: "°C",
      period: period === "2050" ? "2041–2060" : "2081–2100", baseline: "1850–1900",
      range: "Best estimates and very likely ranges",
      method: "IPCC AR6 WGI Table SPM.1. Each value is a 20-year global average for a conditional emissions pathway.",
      caveat: "Pathways have no assigned probabilities. These global averages do not predict a local winter.",
      sources: ["warming"], axisMax: 5, parameters: { pathway, period },
      rows: PATHWAYS.map((p) => ({ label: p.code, ...WARMING[p.key][period], selected: p.key === pathway })),
    };
  }
  if (id === "snow-risk") {
    const snowmaking = choice(params, "snowmaking", ["natural", "snowmaking"] as const, "natural");
    const snowScope = choice(params, "snowScope", ["europe", "regional"] as const, "europe");
    const regional = snowScope === "regional";
    return {
      id, title: regional ? "Snow risk varies across Europe." : "Europe's snow-supply risk rises with warming.",
      seoTitle: "European Ski Snow Risk: Warming & Adaptation",
      description: "Explore published European ski snow-supply risk with and without snowmaking, including study definitions, regional results and source data.",
      subtitle: snowmaking === "natural" ? "Without snowmaking" : "Snowmaking on 50% of slopes",
      unit: "%", period: regional ? "+2°C global warming" : "+2°C and +4°C global warming",
      baseline: "Warming relative to pre-industrial conditions",
      range: "Share of resorts at very high snow-supply risk; no uncertainty interval supplied here",
      method: "François et al. (2023): 2,234 ski resorts in 28 European countries. Adaptation assumes snowmaking on half of slopes. Regional values follow INRAE's study explanation.",
      caveat: "Snow-supply risk is not a probability of resort closure. The French Alps result without snowmaking is approximately one third.",
      sources: regional ? ["ski", "regions"] : ["ski"], axisMax: 100,
      parameters: { snowmaking, snowScope },
      rows: (regional ? REGIONAL_SKI_RISK : SKI_RISK).map((row) => ({
        label: row.name, value: row[snowmaking], low: null, high: null, selected: true,
        ...(regional && row.name === "French Alps" && snowmaking === "natural" ? { qualifier: "approximate" as const } : {}),
      })),
    };
  }
  if (id === "sea-level") {
    const seaYear = choice(params, "seaYear", ["2050", "2100"] as const, "2100");
    const seaPathway = choice(params, "seaPathway", PATHWAY_KEYS, "middle");
    return {
      id, title: "Sea level keeps rising.", seoTitle: "Sea-Level Rise Projections: Compare Pathways",
      description: "Compare IPCC global sea-level rise projections for 2050 and 2100, with medians, likely ranges, baseline definitions and source downloads.",
      subtitle: "Global mean sea-level rise", unit: "cm", period: seaYear, baseline: "1995–2014",
      range: "Medians and likely ranges; medium-confidence processes",
      method: "Final IPCC AR6 WGI Table 9.9. Values in metres are expressed as centimetres without additional modelling.",
      caveat: "Local sea level can differ. These ranges do not include every low-likelihood ice-sheet outcome.",
      sources: ["sea"], axisMax: 100, parameters: { seaYear, seaPathway },
      rows: PATHWAYS.map((p) => ({ label: p.code, ...SEA_LEVEL[p.key][seaYear], selected: p.key === seaPathway })),
    };
  }
  if (id === "sea-temperature") {
    const temperature = choice(params, "temperature", PATHWAY_KEYS, "middle");
    return {
      id, title: "A warmer sea, even between heatwaves.",
      seoTitle: "Sea Temperature Projections: Global Warming",
      description: "Explore global sea-surface warming projections by emissions pathway, with model means, 5–95% ranges, source links and downloadable data.",
      subtitle: "Global mean sea-surface temperature change", unit: "°C", period: "2081–2100", baseline: "1995–2014",
      range: "Model means and 5–95% model ranges",
      method: "IPCC AR6 WGI section 9.2.1.1 and Figure 9.3. The detailed CMIP6 assessment uses a recent baseline, unlike the pre-industrial global warming chart.",
      caveat: "This is a temperature change, not absolute water temperature or a forecast for a particular beach.",
      sources: ["temperature"], axisMax: 4, parameters: { temperature },
      rows: SEA_TEMPERATURE.map((row, index) => ({ label: row.name, value: row.value, low: row.low, high: row.high, selected: PATHWAY_KEYS[index] === temperature })),
    };
  }
  if (id === "marine-heatwaves") {
    const heat = choice(params, "heat", ["low", "very-high"] as const, "low");
    return {
      id, title: "Marine heatwaves become more frequent.",
      seoTitle: "Marine Heatwaves: Future Frequency Compared",
      description: "Compare published global marine heatwave frequency ranges under lower and very high emissions, with baselines, limitations and source data.",
      subtitle: "Global marine heatwave frequency", unit: "×", period: "2081–2100", baseline: "1995–2014 frequency",
      range: "Reported projection ranges; no central estimate supplied",
      method: "IPCC AR6 WGI section 12.4.7 and Box 9.2. Multipliers compare future frequency with the reference period.",
      caveat: "The higher pathway here is SSP5-8.5, unlike SSP3-7.0 in the warming chart. This is not a beach-level prediction.",
      sources: ["heat"], axisMax: 20, parameters: { heat },
      rows: MARINE_HEATWAVES.map((row, index) => ({ label: row.name, value: null, low: row.low, high: row.high, selected: index === (heat === "low" ? 0 : 1) })),
    };
  }
  if (id === "coral") {
    const coral = choice(params, "coral", ["1.5", "2"] as const, "1.5");
    return {
      id, title: "Half a degree matters to a living reef.",
      seoTitle: "Coral Reef Decline: Compare Warming Levels",
      description: "Compare IPCC warm-water coral reef decline at 1.5°C and 2°C warming, with confidence statements, the assessment baseline and source links.",
      subtitle: "Further decline in warm-water coral reefs", unit: "%", period: "+1.5°C and +2°C global warming",
      baseline: "2018 assessment's present-day reefs; warming versus pre-industrial levels",
      range: "70–90% at 1.5°C: high confidence; more than 99% at 2°C: very high confidence",
      method: "IPCC Special Report on Global Warming of 1.5°C, Summary for Policymakers. The greater-than sign is part of the reported finding; 99% is not an exact estimate.",
      caveat: "These are projected declines from the assessment baseline, not measured reef loss since 2026.",
      sources: ["coral"], axisMax: 100, parameters: { coral },
      rows: [
        { label: "+1.5°C warming", value: null, low: 70, high: 90, selected: coral === "1.5" },
        { label: "+2°C warming", value: 99, low: null, high: null, qualifier: "greater-than", selected: coral === "2" },
      ],
    };
  }
  const elevation = choice(params, "elevation", ["0", "1", "2", "3"] as const, "0");
  return {
    id, title: "Snow reliability depends on elevation.",
    seoTitle: "Natural Snow Reliability: Regional Elevations",
    description: "Explore published regional natural-snow reliability elevations in the Alps and Pyrenees, with scenario ranges, study criteria and source data.",
    subtitle: "Regional median groomed natural-snow reliability elevation", unit: "m", period: "2030–2050",
    baseline: "100-day snow criterion in seven winters out of ten",
    range: "Spread of regional medians across RCP2.6, RCP4.5 and RCP8.5, not uncertainty percentiles",
    method: "Spandre et al. (2019), section 3.1.2. At least 100 days with 100 kg/m² of groomed natural snow between 15 December and 15 April, in seven winters out of ten.",
    caveat: "These regional results are not universal snowlines or resort viability cut-offs. Aspect and management matter.",
    sources: ["elevation"], axisMax: 3000, parameters: { elevation },
    rows: ELEVATIONS.map((row, index) => ({ label: row.name, value: null, low: row.low, high: row.high, selected: index === Number(elevation) })),
  };
}

export function climateFindingQuery(finding: ClimateFinding): string {
  return new URLSearchParams(finding.parameters).toString();
}

export function climateFindingPath(finding: ClimateFinding): string {
  return `/climate/charts/${finding.id}?${climateFindingQuery(finding)}`;
}

export function climateObservatoryPath(finding: ClimateFinding): string {
  const anchor = finding.id === "warming" ? "global"
    : ["snow-risk", "snowline", "coral"].includes(finding.id) ? "sports"
      : finding.id === "snow-elevation" || finding.id === "season-length" ? finding.id : "ocean";
  const query = new URLSearchParams(finding.parameters);
  if (finding.id === "coral") query.set("sport", "surf");
  if (["snow-risk", "snowline"].includes(finding.id)) query.set("sport", "ski");
  return `/climate?${query}#${anchor}`;
}

/** Calendar coverage is intentionally absent for warming-level experiments. */
export function climateFindingDataset(finding: ClimateFinding) {
  const url = `https://peakwave.co${climateFindingPath(finding)}`;
  const calendar: Partial<Record<ClimateChartId, string | readonly string[]>> = {
    warming: finding.parameters.period === "2050" ? "2041/2060" : "2081/2100",
    "sea-level": finding.parameters.seaYear,
    "sea-temperature": "2081/2100",
    "marine-heatwaves": "2081/2100",
    snowline: "2030/2050",
    "snow-elevation": ["1986/2005", "2030/2050", "2080/2100"],
    "season-length": ["1999/2012", "2020/2049", "2045/2074", "2070/2099"],
  };
  const spatial = finding.id === "snow-risk" ? (finding.parameters.snowScope === "regional" ? "French Alps, Pyrenees and French / Swiss mid-mountains" : "Europe: 28 countries")
    : finding.id === "snowline" ? "French Alps and French / Spanish / Andorran Pyrenees"
      : finding.id === "snow-elevation" ? (() => { const m = SNOW_MASSIFS.find((massif) => massif.id === finding.parameters.snowMassif)!; return `${m.name}, ${m.region}`; })()
        : finding.id === "season-length" ? (() => { const s = SNOW_DAY_STATIONS.find((station) => station.id === finding.parameters.seasonStation)!; return `${s.name}, ${s.region}, Switzerland`; })() : "Global";
  const quantity: Record<ClimateChartId, string> = {
    warming: "Global surface temperature change",
    "snow-risk": "Share of ski resorts at very high snow-supply risk",
    "sea-level": "Global mean sea-level rise",
    "sea-temperature": "Global mean sea-surface temperature change",
    "marine-heatwaves": "Global marine heatwave frequency multiplier",
    coral: "Further decline in warm-water coral reefs",
    snowline: "Regional median groomed natural-snow reliability elevation",
    "snow-elevation": "Groomed natural-snow reliability elevation at the 70% annual reliability criterion",
    "season-length": "Mean days with at least 5 cm of snow",
  };
  return {
    "@context": "https://schema.org", "@type": "Dataset", "@id": `${url}#dataset`,
    name: `${finding.subtitle}: published climate evidence`,
    description: `${finding.description} A PeakWave compilation of the cited research. ${finding.caveat}`,
    url, creator: { "@type": "Organization", name: "PeakWave", url: "https://peakwave.co" },
    isAccessibleForFree: true, inLanguage: "en-GB", dateModified: REVIEWED_ON,
    isBasedOn: finding.sources.map((key) => FINDING_SOURCES[key].url),
    citation: finding.sources.map((key) => ({ "@type": "CreativeWork", name: `${FINDING_SOURCES[key].label} (${FINDING_SOURCES[key].year})`, url: FINDING_SOURCES[key].url })),
    measurementTechnique: `${finding.method} Baseline: ${finding.baseline}. Range: ${finding.range}.`,
    variableMeasured: [{ "@type": "PropertyValue", name: quantity[finding.id], unitText: finding.unit }],
    spatialCoverage: { "@type": "Place", name: spatial },
    ...(calendar[finding.id] ? { temporalCoverage: calendar[finding.id] } : {}),
    distribution: [{ "@type": "DataDownload", encodingFormat: "text/csv", contentUrl: `https://peakwave.co/api/climate/chart-export?chart=${finding.id}&format=csv&${climateFindingQuery(finding)}` }],
  };
}

export function climateRowValue(row: ClimateFindingRow, unit: string): string {
  const n = (value: number) => value.toLocaleString("en-GB", { maximumFractionDigits: 2 });
  if (row.value !== null) {
    const prefix = row.qualifier === "greater-than" ? ">" : row.qualifier === "approximate" ? "≈" : "";
    const range = row.low !== null && row.high !== null ? ` (${n(row.low)}–${n(row.high)})` : "";
    return `${prefix}${n(row.value)}${range} ${unit}`;
  }
  return row.low !== null && row.high !== null ? `${n(row.low)}–${n(row.high)} ${unit}` : "Not available";
}

function csvCell(value: string | number | null): string {
  if (value === null) return '""';
  const raw = typeof value === "number" ? String(value) : (/^[\s]*[=+@\-]/u.test(value) ? `'${value}` : value);
  return `"${raw.replaceAll('"', '""')}"`;
}

/** Each data row remains independently attributable after spreadsheet sorting. */
export function climateFindingCsv(finding: ClimateFinding): string {
  const sources = finding.sources.map((key) => FINDING_SOURCES[key]);
  const headers = ["chart", "series", "display_value", "central_value_or_threshold", "lower_bound", "upper_bound", "qualifier", "selected", "unit", "period", "baseline", "range_definition", "method", "caveat", "comparison_reference", "source_titles", "source_publication_years", "source_urls", "source_notes", "evidence_reviewed", "reuse", "permalink"];
  const rows = finding.rows.map((row) => [
    finding.title, row.label, climateRowValue(row, finding.unit), row.value, row.low, row.high,
    row.qualifier ?? "", row.selected ? "yes" : "no", finding.unit, finding.period, finding.baseline,
    finding.range, finding.method, finding.caveat, finding.reference ? `${finding.reference.label}: ${finding.reference.value} ${finding.unit}` : "", sources.map((s) => s.label).join(" | "),
    sources.map((s) => s.year).join(" | "), sources.map((s) => s.url).join(" | "),
    sources.map((s) => s.note).join(" | "), REVIEWED_ON, CLIMATE_REUSE_NOTE,
    `https://peakwave.co${climateFindingPath(finding)}`,
  ]);
  return `\uFEFF${[headers, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n")}\r\n`;
}

export function escapeClimateMarkup(value: string): string {
  return value.replace(/[&<>"']/gu, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character]!);
}

function wrapText(value: string, limit: number): string[] {
  const lines: string[] = [];
  let line = "";
  for (const word of value.split(/\s+/u)) {
    if (line && line.length + word.length + 1 > limit) { lines.push(line); line = word; }
    else line = line ? `${line} ${word}` : word;
  }
  if (line) lines.push(line);
  return lines;
}

/** Static, script-free SVG with provenance embedded as visible text. */
export function climateFindingSvg(finding: ClimateFinding): string {
  const e = escapeClimateMarkup;
  let y = 55;
  const parts: string[] = [];
  const text = (value: string, size = 21, colour = "#263c50", limit = 96) => {
    for (const line of wrapText(value, limit)) {
      parts.push(`<text x="54" y="${y}" font-size="${size}" fill="${colour}">${e(line)}</text>`);
      y += size + 10;
    }
  };
  text("PEAKWAVE / CLIMATE OBSERVATORY", 17, "#087684");
  y += 12;
  text(finding.title, 36, "#16233b", 52);
  text(`${finding.subtitle} · ${finding.unit}`, 22);
  y += 12;
  text(`Period: ${finding.period}`, 19);
  text(`Baseline: ${finding.baseline}`, 19);
  text(finding.range, 19, "#465c70");
  if (finding.reference) text(`${finding.reference.label}: ${finding.reference.value} ${finding.unit} (dashed marker)`, 19);
  y += 30;
  for (const row of finding.rows) {
    parts.push(`<text x="54" y="${y}" font-size="22" fill="#16233b">${e(row.label)}${row.selected ? " · selected" : ""}</text>`);
    parts.push(`<text x="1146" y="${y}" text-anchor="end" font-size="22" fill="#16233b">${e(climateRowValue(row, finding.unit))}</text>`);
    y += 27;
    const left = 54, width = 1092;
    parts.push(`<line x1="${left}" y1="${y}" x2="${left + width}" y2="${y}" stroke="#dbe2e5" stroke-width="10"/>`);
    if (finding.reference) {
      const referenceX = left + finding.reference.value / finding.axisMax * width;
      parts.push(`<line x1="${referenceX}" y1="${y - 18}" x2="${referenceX}" y2="${y + 18}" stroke="#a64f35" stroke-width="2" stroke-dasharray="3 3"/>`);
    }
    if (row.qualifier !== "greater-than") {
      const low = row.low ?? 0, high = row.high ?? row.value ?? 0;
      parts.push(`<line x1="${left + low / finding.axisMax * width}" y1="${y}" x2="${left + high / finding.axisMax * width}" y2="${y}" stroke="${row.selected ? "#16808f" : "#90bfc5"}" stroke-width="12"/>`);
    }
    if (row.value !== null) {
      const x = left + row.value / finding.axisMax * width;
      if (row.qualifier === "greater-than") {
        parts.push(`<circle cx="${x}" cy="${y}" r="8" fill="#f6f8f5" stroke="#16808f" stroke-width="3"/><path d="M${x + 4} ${y - 13}l12 13-12 13" fill="none" stroke="#16808f" stroke-width="3"/>`);
      } else parts.push(`<line x1="${x}" y1="${y - 12}" x2="${x}" y2="${y + 12}" stroke="#16233b" stroke-width="4"/>`);
    }
    y += 64;
  }
  y -= 20;
  parts.push(`<text x="54" y="${y}" font-size="18" fill="#465c70">0</text><text x="1146" y="${y}" text-anchor="end" font-size="18" fill="#465c70">${finding.axisMax.toLocaleString("en-GB")} ${e(finding.unit)}</text>`);
  y += 46;
  text(finding.method, 19);
  y += 8;
  text(finding.caveat, 20, "#16233b");
  y += 14;
  for (const key of finding.sources) {
    const source = FINDING_SOURCES[key];
    text(`${source.label} (${source.year})`, 18, "#087684");
    text(source.url, 16, "#465c70", 112);
    if ("licence" in source) text(`Source licence: ${source.licence}`, 16, "#465c70");
  }
  y += 8;
  text(CLIMATE_REUSE_NOTE, 17);
  text(`Evidence reviewed ${REVIEWED_ON}`, 17);
  text(`https://peakwave.co${climateFindingPath(finding)}`, 16, "#465c70", 112);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 ${y + 24}" width="1200" height="${y + 24}" role="img" aria-labelledby="chart-title chart-description"><title id="chart-title">${e(finding.title)}</title><desc id="chart-description">${e(`${finding.period}. ${finding.range}. ${finding.caveat}`)}</desc><rect width="1200" height="${y + 24}" fill="#f6f8f5"/><g font-family="Arial, sans-serif">${parts.join("")}</g></svg>`;
}
